import { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useCommandPaletteStore } from '../../store/commandPaletteStore';
import { cn } from '../../utils/cn';

interface SearchResult {
  type: string;
  title: string;
  href: string;
}

const MOCK_RESULTS: SearchResult[] = [
  { type: 'Page', title: 'Dashboard', href: '/dashboard' },
  { type: 'Page', title: 'File Manager', href: '/files' },
  { type: 'Page', title: 'Pricing', href: '/pricing' },
  { type: 'AI Tool', title: 'AI Detector', href: '/ai-tools/detector' },
  { type: 'AI Tool', title: 'Humanizer', href: '/ai-tools/humanizer' },
  { type: 'AI Tool', title: 'Journal Finder', href: '/ai-tools/journal-finder' },
];

const TYPE_ORDER = ['Page', 'AI Tool'];

const TYPE_ICONS: Record<string, JSX.Element> = {
  Page: (
    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
  ),
  'AI Tool': (
    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" /></svg>
  ),
  People: (
    <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>
  ),
};

export function CommandPalette() {
  const { isOpen, close } = useCommandPaletteStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);

  // Filter + group results
  const filtered = useMemo(() => {
    if (!query.trim()) return MOCK_RESULTS;
    return MOCK_RESULTS.filter((r) =>
      r.title.toLowerCase().includes(query.toLowerCase())
    );
  }, [query]);

  const grouped = useMemo(() => {
    const map: Record<string, SearchResult[]> = {};
    for (const result of filtered) {
      if (!map[result.type]) map[result.type] = [];
      map[result.type].push(result);
    }
    return map;
  }, [filtered]);

  const flatFiltered = useMemo(() => filtered, [filtered]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [isOpen]);

  // Scroll active into view
  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        close();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % flatFiltered.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + flatFiltered.length) % flatFiltered.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatFiltered[activeIndex];
        if (item) {
          navigate(item.href);
          close();
        }
      }
    }

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, flatFiltered, activeIndex, navigate, close]);

  function handleSelect(href: string) {
    navigate(href);
    close();
  }

  // Build flat index per result (for keyboard tracking across groups)
  let globalIndex = 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onMouseDown={() => close()}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4" onMouseDown={() => close()}>
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -8 }}
            transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
            className="w-full max-w-xl"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="rounded-2xl border border-neutral-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-2xl overflow-hidden">
              {/* Search input */}
              <div className="flex items-center gap-3 border-b border-gray-100 dark:border-zinc-800 px-4 py-3">
                <svg className="h-4 w-4 shrink-0 text-gray-400 dark:text-zinc-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
                  placeholder="Search pages, tools, people..."
                  className="flex-1 bg-transparent text-sm text-gray-900 dark:text-zinc-100 placeholder-gray-400 dark:placeholder-zinc-500 outline-none"
                />
                {query && (
                  <button onClick={() => setQuery('')} className="text-gray-400 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-300 transition-colors">
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                )}
              </div>

              {/* Results */}
              <div className="max-h-80 overflow-y-auto">
                {flatFiltered.length === 0 ? (
                  <p className="py-10 text-center text-sm text-gray-400 dark:text-zinc-500">
                    No results for "{query}"
                  </p>
                ) : (
                  TYPE_ORDER.filter((type) => grouped[type]).map((type) => {
                    const items = grouped[type];
                    return (
                      <div key={type}>
                        <p className="sticky top-0 bg-white dark:bg-zinc-900 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-zinc-500">
                          {type}
                        </p>
                        {items.map((result) => {
                          const idx = globalIndex++;
                          const isActive = idx === activeIndex;
                          return (
                            <button
                              key={result.href}
                              ref={isActive ? activeItemRef : undefined}
                              onClick={() => handleSelect(result.href)}
                              onMouseEnter={() => setActiveIndex(idx)}
                              className={cn(
                                'flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors',
                                isActive
                                  ? 'bg-gray-50 dark:bg-zinc-800'
                                  : 'hover:bg-gray-50 dark:hover:bg-zinc-800/60'
                              )}
                            >
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-100 dark:bg-zinc-800 text-gray-500 dark:text-zinc-400">
                                {TYPE_ICONS[type]}
                              </span>
                              <span className="flex-1 text-sm font-medium text-gray-900 dark:text-zinc-100">
                                {result.title}
                              </span>
                              <span className="rounded-md bg-gray-100 dark:bg-zinc-800 px-2 py-0.5 text-xs text-gray-500 dark:text-zinc-400">
                                {type}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center gap-4 border-t border-gray-100 dark:border-zinc-800 px-4 py-2 text-xs text-gray-400 dark:text-zinc-500">
                <span className="flex items-center gap-1"><kbd className="rounded border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800 px-1.5 py-0.5 font-mono">↑↓</kbd> navigate</span>
                <span className="flex items-center gap-1"><kbd className="rounded border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800 px-1.5 py-0.5 font-mono">↵</kbd> select</span>
                <span className="flex items-center gap-1"><kbd className="rounded border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-800 px-1.5 py-0.5 font-mono">Esc</kbd> dismiss</span>
              </div>
            </div>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
